import Modal from "../../Modal";
import { DialogWrapper } from "../../containers";
import { TitleH2 } from "../../generic";
import Calendar, { useCalendar } from "../../Calendar";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TUserShort } from "../../../interfaces/interfacesChat";
import { getPeriod, isoDate } from "../../../utils/helpers";
import {
  useSendInvite,
  useUpdateOfferedDates
} from "../../../API/queries/hostApiQueries";
import { Button as AntButton, Skeleton } from "antd";
import { useNotifier } from "../../../context/ContextNotifier";
import { useGetMatch } from "../../../API/queries/studentApiQueries";
import { MatchStatusEnum, NO_END_DATE } from "../../../utils/constants";
import { parseServerDateTime } from "../../../utils/time";
import styled from "styled-components/macro";

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  button {
    flex: 1;
    margin: 5px;
    padding: 10px;
  }
`;

export const SendInviteModal: React.FC<{
  hostId: string;
  houseId: string;
  student: TUserShort;
  isShow: boolean;
  onClose: () => void;
  onSent: () => void;
}> = ({ hostId, houseId, student, isShow, onClose, onSent }) => {
  const sendInvite = useSendInvite();
  const updateInvite = useUpdateOfferedDates();
  const notifier = useNotifier();
  const { data: match, isLoading: isLoadingMatch } = useGetMatch(
    student.user_id,
    hostId,
    houseId
  );
  const checkInDate = match?.offered_check_in_date ?? match?.check_in_date;
  const checkOutDate = match?.offered_check_out_date ?? match?.check_out_date;
  const initialStartDate = checkInDate
    ? parseServerDateTime(checkInDate)
    : undefined;
  const initialEndDate = checkOutDate
    ? parseServerDateTime(checkOutDate)
    : undefined;

  const { t: t_match } = useTranslation("matches");
  const [inviteError, setInviteError] = useState("");

  const initialValues = useMemo(() => {
    const defaultValues = getPeriod();
    if (initialStartDate) {
      defaultValues.value = initialStartDate;
    }
    if (initialEndDate) {
      defaultValues.endInterval = initialEndDate;
    }
    return defaultValues;
  }, [initialStartDate, initialEndDate]);

  const dateRange = useCalendar({
    ...initialValues,
    range: true
  });

  useEffect(() => {
    if (!dateRange.value) {
      setInviteError(t_match("host.startAndEnd"));
    } else if (
      dateRange.value?.toISOString().slice(0, -7) ===
      dateRange.endInterval?.toISOString().slice(0, -7)
    ) {
      setInviteError(t_match("host.sameDate"));
    } else {
      setInviteError("");
    }
  }, [dateRange.endInterval, dateRange.value, t_match]);

  const handleSendInvite = () => {
    if (!match) {
      return;
    }
    const payload = {
      host_id: hostId,
      house_id: houseId,
      student_id: student.user_id,
      student_check_in_date: isoDate(dateRange.value),
      student_check_out_date: !dateRange.endInterval
        ? isoDate(NO_END_DATE)
        : isoDate(dateRange.endInterval)
    };

    if (match.match_status === MatchStatusEnum.CHATTED) {
      sendInvite.mutate(payload, {
        onSuccess: () => {
          notifier.success({
            message: t_match("sendInviteModal.inviteSent")
          });
          onSent();
        }
      });
    } else if (
      match.match_status === MatchStatusEnum.INVITED ||
      match.match_status === MatchStatusEnum.ACCEPTED ||
      match.match_status === MatchStatusEnum.PENDING ||
      match.match_status === MatchStatusEnum.ACTIVE
    ) {
      updateInvite.mutate(payload, {
        onSuccess: () => {
          notifier.success({
            message: t_match("sendInviteModal.inviteUpdated")
          });
          onSent();
        }
      });
    }
  };

  if (isLoadingMatch) {
    return <Skeleton active={true} />;
  }

  return (
    <Modal handleClose={onClose} isShow={isShow} className="sendInviteModal">
      <DialogWrapper>
        <TitleH2>{t_match("host.dateRange.title")}</TitleH2>
        <div>
          <div className="cal">
            <Calendar
              {...dateRange}
              error={inviteError}
              isPossibleNoEndDateRange
            />
            <div
              style={{
                textAlign: "center",
                marginTop: "2px",
                color: "red",
                wordBreak: "break-word"
              }}
            >
              {inviteError}
            </div>
          </div>
        </div>
        <ButtonContainer>
          <AntButton
            type="default"
            onClick={() => handleSendInvite()}
            size="large"
            loading={sendInvite.isLoading || updateInvite.isLoading}
            disabled={
              !!inviteError || typeof dateRange.endInterval !== "undefined"
            }
          >
            {t_match("host.dateRange.noEndDate")}
          </AntButton>
          <AntButton
            type={"primary"}
            disabled={!!inviteError || !dateRange.endInterval}
            onClick={() => handleSendInvite()}
            loading={sendInvite.isLoading || updateInvite.isLoading}
            size="large"
          >
            {t_match("host.dateRange.buttonPropose")}
          </AntButton>
        </ButtonContainer>
      </DialogWrapper>
    </Modal>
  );
};
